import { useState, useEffect } from 'react'

interface CurrencyInfo {
  code: string
  symbol: string
  rate: number // calibrated rate vs USD (includes margin over payment gateway)
  locale: string
}

const CURRENCY_MAP: Record<string, CurrencyInfo> = {
  AR: { code: 'ARS', symbol: '$', rate: 1445, locale: 'es-AR' },
  CL: { code: 'CLP', symbol: '$', rate: 1144, locale: 'es-CL' },
  MX: { code: 'MXN', symbol: '$', rate: 22.5, locale: 'es-MX' },
  CO: { code: 'COP', symbol: '$', rate: 4325, locale: 'es-CO' },
  PE: { code: 'PEN', symbol: 'S/', rate: 4.52, locale: 'es-PE' },
  UY: { code: 'UYU', symbol: '$', rate: 48.17, locale: 'es-UY' },
  PY: { code: 'PYG', symbol: '₲', rate: 9031, locale: 'es-PY' },
  BO: { code: 'BOB', symbol: 'Bs', rate: 8.3, locale: 'es-BO' },
  CR: { code: 'CRC', symbol: '₡', rate: 614, locale: 'es-CR' },
  GT: { code: 'GTQ', symbol: 'Q', rate: 9.39, locale: 'es-GT' },
  HN: { code: 'HNL', symbol: 'L', rate: 30.1, locale: 'es-HN' },
  NI: { code: 'NIO', symbol: 'C$', rate: 43.95, locale: 'es-NI' },
  DO: { code: 'DOP', symbol: 'RD$', rate: 69.84, locale: 'es-DO' },
  ES: { code: 'EUR', symbol: '€', rate: 1.11, locale: 'es-ES' },
  BR: { code: 'BRL', symbol: 'R$', rate: 6.15, locale: 'pt-BR' },
  // Dollarized countries
  EC: { code: 'USD', symbol: '$', rate: 1, locale: 'es-EC' },
  PA: { code: 'USD', symbol: '$', rate: 1, locale: 'es-PA' },
  SV: { code: 'USD', symbol: '$', rate: 1, locale: 'es-SV' },
  VE: { code: 'USD', symbol: '$', rate: 1, locale: 'es-VE' },
  US: { code: 'USD', symbol: '$', rate: 1, locale: 'en-US' },
}

const DEFAULT_CURRENCY: CurrencyInfo = {
  code: 'USD',
  symbol: '$',
  rate: 1,
  locale: 'en-US',
}

function applyCharmPricing(rawPrice: number): number {
  if (rawPrice >= 100000) {
    // e.g., 203,000 → 202,900 (round to thousands - 100)
    return Math.floor(rawPrice / 1000) * 1000 - 100
  }
  if (rawPrice >= 10000) {
    // e.g., 16,200 → 15,900 (round to thousands - 100)
    return Math.floor(rawPrice / 1000) * 1000 - 100
  }
  if (rawPrice >= 1000) {
    // e.g., 1,520 → 1,490 (round to hundreds - 10)
    return Math.floor(rawPrice / 100) * 100 - 10
  }
  if (rawPrice >= 100) {
    // e.g., 315 → 299 (round to tens - 1)
    return Math.floor(rawPrice / 10) * 10 - 1
  }
  if (rawPrice >= 10) {
    // e.g., 50.3 → 49.99 (floor - 0.01)
    return Math.floor(rawPrice) - 0.01
  }
  // < 10: e.g., 6.2 → 6.99 (floor + 0.99)
  return Math.floor(rawPrice) + 0.99
}

function countryToFlag(countryCode: string): string {
  return countryCode
    .toUpperCase()
    .split('')
    .map((char) => String.fromCodePoint(char.charCodeAt(0) + 127397))
    .join('')
}

// Timezone-based country detection (works without any API call)
function detectCountryFromTimezone(): string | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
    const tzToCountry: Record<string, string> = {
      'America/Argentina/Buenos_Aires': 'AR',
      'America/Argentina/Cordoba': 'AR',
      'America/Argentina/Salta': 'AR',
      'America/Argentina/Tucuman': 'AR',
      'America/Argentina/Mendoza': 'AR',
      'America/Argentina/San_Juan': 'AR',
      'America/Argentina/Jujuy': 'AR',
      'America/Argentina/Catamarca': 'AR',
      'America/Argentina/La_Rioja': 'AR',
      'America/Argentina/San_Luis': 'AR',
      'America/Argentina/Rio_Gallegos': 'AR',
      'America/Argentina/Ushuaia': 'AR',
      'America/Buenos_Aires': 'AR',
      'America/Santiago': 'CL',
      'America/Mexico_City': 'MX',
      'America/Cancun': 'MX',
      'America/Merida': 'MX',
      'America/Monterrey': 'MX',
      'America/Matamoros': 'MX',
      'America/Chihuahua': 'MX',
      'America/Hermosillo': 'MX',
      'America/Mazatlan': 'MX',
      'America/Tijuana': 'MX',
      'America/Bogota': 'CO',
      'America/Lima': 'PE',
      'America/Montevideo': 'UY',
      'America/Asuncion': 'PY',
      'America/La_Paz': 'BO',
      'America/Costa_Rica': 'CR',
      'America/Guatemala': 'GT',
      'America/Tegucigalpa': 'HN',
      'America/Managua': 'NI',
      'America/Santo_Domingo': 'DO',
      'America/Guayaquil': 'EC',
      'America/Panama': 'PA',
      'America/El_Salvador': 'SV',
      'America/Caracas': 'VE',
      'America/Sao_Paulo': 'BR',
      'America/Fortaleza': 'BR',
      'America/Recife': 'BR',
      'America/Bahia': 'BR',
      'America/Belem': 'BR',
      'America/Manaus': 'BR',
      'America/Cuiaba': 'BR',
      'America/Campo_Grande': 'BR',
      'America/Porto_Velho': 'BR',
      'America/Rio_Branco': 'BR',
      'Europe/Madrid': 'ES',
      'Atlantic/Canary': 'ES',
      'America/New_York': 'US',
      'America/Chicago': 'US',
      'America/Denver': 'US',
      'America/Los_Angeles': 'US',
      'America/Phoenix': 'US',
      'America/Anchorage': 'US',
      'Pacific/Honolulu': 'US',
    }
    if (tzToCountry[tz]) return tzToCountry[tz]
    // Partial match: check if timezone starts with known region
    if (tz.startsWith('America/Argentina')) return 'AR'
    if (tz.startsWith('America/Mexico') || tz.includes('Mexico')) return 'MX'
  } catch {}
  return null
}

function detectCountryFromLocale(): string | null {
  try {
    const lang = navigator.language || (navigator as any).userLanguage || ''
    // Format: "es-AR", "pt-BR", etc.
    const parts = lang.split('-')
    if (parts.length >= 2) {
      const country = parts[1].toUpperCase()
      if (CURRENCY_MAP[country]) return country
    }
    // Fallback: map language-only codes to most common country
    const langCode = parts[0].toLowerCase()
    const langToCountry: Record<string, string> = {
      es: 'MX',
      pt: 'BR',
      en: 'US',
      fr: 'FR',
      de: 'DE',
      it: 'IT',
    }
    if (langToCountry[langCode]) return langToCountry[langCode]
  } catch {}
  return null
}

const SESSION_KEY = 'mp_visitor_country'

export function useLocalCurrency() {
  // Start with best available detection immediately so we don't flash USD
  const initialCountry = (() => {
    if (typeof window === 'undefined') return 'US'
    const cached = sessionStorage.getItem(SESSION_KEY)
    if (cached) return cached
    // Try timezone first (most reliable without API), then locale
    return detectCountryFromTimezone() || detectCountryFromLocale() || 'US'
  })()

  const [countryCode, setCountryCode] = useState<string>(initialCountry)
  const [isLoading, setIsLoading] = useState(
    !sessionStorage.getItem(SESSION_KEY),
  )

  useEffect(() => {
    // If already cached, skip API call
    const cached = sessionStorage.getItem(SESSION_KEY)
    if (cached) {
      setCountryCode(cached)
      setIsLoading(false)
      return
    }

    // Try ipapi.co first, then ip-api.com as fallback
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 4000)

    fetch('https://ipapi.co/json/', { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error('ipapi failed')
        return res.json()
      })
      .then((data) => {
        const code = data?.country_code?.toUpperCase() || null
        if (code) {
          sessionStorage.setItem(SESSION_KEY, code)
          setCountryCode(code)
          setIsLoading(false)
        } else {
          throw new Error('No country code')
        }
      })
      .catch(() => {
        // Second attempt: try ip-api.com
        fetch('http://ip-api.com/json/?fields=countryCode', {
          signal: controller.signal,
        })
          .then((res) => res.json())
          .then((data) => {
            const code = data?.countryCode?.toUpperCase() || null
            if (code) {
              sessionStorage.setItem(SESSION_KEY, code)
              setCountryCode(code)
            } else {
              // Final fallback: timezone + locale (already set as initial)
              const tzCountry = detectCountryFromTimezone()
              const localeCountry = detectCountryFromLocale()
              const fallback = tzCountry || localeCountry
              if (fallback) {
                sessionStorage.setItem(SESSION_KEY, fallback)
                setCountryCode(fallback)
              }
            }
          })
          .catch(() => {
            // Use timezone/locale fallback (already set as initial state)
            const tzCountry = detectCountryFromTimezone()
            const localeCountry = detectCountryFromLocale()
            const fallback = tzCountry || localeCountry
            if (fallback) {
              sessionStorage.setItem(SESSION_KEY, fallback)
              setCountryCode(fallback)
            }
          })
          .finally(() => setIsLoading(false))
      })

    return () => {
      clearTimeout(timeout)
      controller.abort()
    }
  }, [])

  const currencyInfo = CURRENCY_MAP[countryCode] || DEFAULT_CURRENCY
  const isUSD = currencyInfo.code === 'USD'

  function formatPrice(usdAmount: number): string {
    const converted = usdAmount * currencyInfo.rate
    const charmed = isUSD ? usdAmount : applyCharmPricing(converted)

    try {
      const formatted = new Intl.NumberFormat(currencyInfo.locale, {
        style: 'currency',
        currency: currencyInfo.code,
        minimumFractionDigits: charmed % 1 === 0 ? 0 : 2,
        maximumFractionDigits: charmed >= 100 ? 0 : 2,
      }).format(charmed)

      // Append currency code for non-USD
      if (!isUSD) {
        return `${formatted} ${currencyInfo.code}`
      }
      return formatted
    } catch {
      // Fallback formatting
      const num = charmed >= 100 ? Math.round(charmed) : charmed.toFixed(2)
      if (!isUSD) {
        return `${currencyInfo.symbol}${num} ${currencyInfo.code}`
      }
      return `${currencyInfo.symbol}${num}`
    }
  }

  function formatPriceCompact(usdAmount: number): string {
    const converted = usdAmount * currencyInfo.rate
    const charmed = isUSD ? usdAmount : applyCharmPricing(converted)

    if (charmed >= 10000) {
      const k = (charmed / 1000).toFixed(1).replace('.0', '')
      return `${currencyInfo.symbol}${k}k`
    }
    if (charmed >= 1000) {
      const k = (charmed / 1000).toFixed(1).replace('.0', '')
      return `${currencyInfo.symbol}${k}k`
    }

    try {
      return new Intl.NumberFormat(currencyInfo.locale, {
        style: 'currency',
        currency: currencyInfo.code,
        minimumFractionDigits: charmed % 1 === 0 ? 0 : 2,
        maximumFractionDigits: charmed >= 100 ? 0 : 2,
      }).format(charmed)
    } catch {
      const num = charmed >= 100 ? Math.round(charmed) : charmed.toFixed(2)
      return `${currencyInfo.symbol}${num}`
    }
  }

  return {
    formatPrice,
    formatPriceCompact,
    currencyCode: currencyInfo.code,
    currencySymbol: currencyInfo.symbol,
    countryCode,
    countryFlag: countryToFlag(countryCode),
    isLoading,
    isUSD,
  }
}
